import React, { useState, useEffect } from "react";
import { X, Clock, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { firebaseApp } from "../../firebase";

const TicketDetailModal = ({ ticketId, isOpen, onClose }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const db = getFirestore(firebaseApp);

  useEffect(() => {
    if (isOpen && ticketId) {
      loadTicketDetails();
    }
  }, [isOpen, ticketId]);

  const loadTicketDetails = async () => {
    setLoading(true);
    try {
      const ticketDoc = await getDoc(doc(db, "supportTickets", ticketId));
      if (ticketDoc.exists()) {
        setTicket({ id: ticketDoc.id, ...ticketDoc.data() });
      }
    } catch (error) {
      console.error("Error loading ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  // Define ticket progress stages
  const stages = [
    { key: "open", label: "Ticket Opened", icon: AlertCircle, color: "text-blue-600" },
    { key: "in_progress", label: "In Progress", icon: Clock, color: "text-yellow-600" },
    { key: "resolved", label: "Resolved", icon: CheckCircle, color: "text-green-600" },
    { key: "closed", label: "Closed", icon: XCircle, color: "text-gray-600" },
  ];

  const getStageIndex = (status) => {
    const stageMap = {
      open: 0,
      in_progress: 1,
      resolved: 2,
      closed: 3,
    };
    return stageMap[status] || 0;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-3 text-gray-600">Loading ticket details...</span>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8">
          <p className="text-gray-600">Ticket not found</p>
          <button
            onClick={onClose}
            className="mt-4 text-red-600 hover:text-red-700 font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const currentStageIndex = getStageIndex(ticket.status);
  const progressPercentage = ((currentStageIndex + 1) / stages.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto my-auto">
        {/* Minimal Modal Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-red-700 px-5 sm:px-6 py-4 flex items-start justify-between z-10">
          <div className="flex-1 pr-4">
            <p className="text-red-100 text-xs font-semibold uppercase tracking-wider mb-1">Support Ticket</p>
            <h2 className="text-lg font-bold text-white truncate">{ticket.subject}</h2>
            <p className="text-red-100 text-xs mt-1 font-mono">{ticket.ticketCode}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-red-700/50 rounded-lg transition-colors text-white flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6 space-y-4">
          {/* Description Card */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Description</p>
            <p className="text-sm text-gray-700 leading-relaxed">{ticket.description}</p>
          </div>

          {/* Status Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-1.5">Status</p>
              <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold ${getStatusBadgeColor(ticket.status)}`}>
                {ticket.status.replace("_", " ")}
              </span>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <p className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-1.5">Priority</p>
              <span className="inline-block px-2.5 py-1 rounded-md text-xs font-bold bg-orange-600 text-white capitalize">
                {ticket.priority}
              </span>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <p className="text-xs font-bold text-purple-700 uppercase tracking-wider mb-1.5">Type</p>
              <span className="inline-block px-2.5 py-1 rounded-md text-xs font-bold bg-purple-600 text-white capitalize">
                {ticket.type}
              </span>
            </div>
          </div>

          {/* Progress Tracker - Compact */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Progress</h3>

            {/* Progress Bar */}
            <div className="relative mb-6">
              <div className="absolute top-4 left-0 right-0 h-1.5 bg-gray-300 rounded-full">
                <div
                  className="h-full bg-gradient-to-r from-red-500 via-red-600 to-red-700 rounded-full transition-all duration-700"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>

              {/* Stage Dots */}
              <div className="relative flex justify-between">
                {stages.map((stage, index) => {
                  const Icon = stage.icon;
                  const isCompleted = index <= currentStageIndex;

                  return (
                    <div key={stage.key} className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center border-3 transition-all duration-300 font-bold ${
                          isCompleted
                            ? "bg-gradient-to-br from-red-600 to-red-700 border-red-700 text-white"
                            : "bg-white border-gray-300 text-gray-400"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stage Labels */}
            <div className="grid grid-cols-4 gap-1">
              {stages.map((stage, index) => {
                const isCompleted = index <= currentStageIndex;
                const isCurrent = index === currentStageIndex;

                return (
                  <div key={stage.key} className="text-center">
                    <p
                      className={`text-xs font-bold ${
                        isCurrent
                          ? "text-red-600"
                          : isCompleted
                          ? "text-gray-900"
                          : "text-gray-400"
                      }`}
                    >
                      {stage.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timeline - Compact */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Timeline</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 border-2 border-red-600">
                    <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-gray-900">Created</p>
                  <p className="text-xs text-gray-600 mt-0.5">{formatDate(ticket.createdAt)}</p>
                </div>
              </div>

              {ticket.status !== "open" && (
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 border-2 border-yellow-600">
                      <Clock className="w-3.5 h-3.5 text-yellow-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-gray-900">Updated</p>
                    <p className="text-xs text-gray-600 mt-0.5">{formatDate(ticket.updatedAt)}</p>
                  </div>
                </div>
              )}

              {(ticket.status === "resolved" || ticket.status === "closed") && (
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 border-2 border-green-600">
                      <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-gray-900">
                      {ticket.status === "resolved" ? "Resolved" : "Closed"}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">{formatDate(ticket.updatedAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Info */}
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Ticket Code</p>
              <p className="text-xs font-mono font-bold text-gray-900 truncate">{ticket.ticketCode}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Created</p>
              <p className="text-xs font-bold text-gray-900">{formatDate(ticket.createdAt).split(",")[0]}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailModal;
